import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProfileCard from "@/components/ProfileCard";
import { api, formatApiError } from "@/lib/api";
import { Sliders, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES } from "@/lib/constants";

export default function Search() {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({
    age_min: params.get("age_min") || 22,
    age_max: params.get("age_max") || 35,
    religion: params.get("religion") || "",
    community: "",
    country: params.get("country") || "",
    location: params.get("location") || "",
    education: "",
    profession: "",
    marital_status: "",
    diet: "",
  });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shortlist, setShortlist] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const query = Object.entries(filters).filter(([_, v]) => v !== "" && v != null).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const [res, sl] = await Promise.all([
        api.get(`/matches/search?${query}&limit=24`),
        api.get("/shortlist"),
      ]);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
      setShortlist(sl.data.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendInterest = async (id) => {
    try {
      await api.post("/interests/send", { to_user_id: id });
      toast.success("Interest sent");
    } catch (e) {
      const status = e.response?.status;
      const msg = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        toast.error(msg || "Upgrade to send interests", {
          action: { label: "View plans", onClick: () => window.location.assign("/plans") },
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const toggleShortlist = async (id) => {
    const exists = shortlist.find((s) => s.user_id === id);
    try {
      if (exists) { await api.delete(`/shortlist/${id}`); }
      else { await api.post(`/shortlist/${id}`); }
      const sl = await api.get("/shortlist");
      setShortlist(sl.data.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="search-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="overline">Find your match</p>
        <h1 className="font-serif text-4xl text-brand-ink mt-2">Browse profiles</h1>

        <div className="grid md:grid-cols-12 gap-6 mt-8">
          {/* Filters */}
          <aside className="md:col-span-3">
            <div className="card-elegant p-5 sticky top-24">
              <div className="flex items-center gap-2 mb-4"><Sliders className="h-4 w-4 text-brand-gold" /> <span className="overline">Filters</span></div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-brand-inkLight">Age</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <input type="number" value={filters.age_min} onChange={(e) => setFilters({ ...filters, age_min: e.target.value })} className="input-elegant" min="18" data-testid="filter-age-min" />
                    <input type="number" value={filters.age_max} onChange={(e) => setFilters({ ...filters, age_max: e.target.value })} className="input-elegant" max="80" data-testid="filter-age-max" />
                  </div>
                </div>
                <Select label="Religion" value={filters.religion} onChange={(v) => setFilters({ ...filters, religion: v })} options={["", "Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Parsi", "Other"]} testId="filter-religion" />
                <TextInput label="Community" value={filters.community} onChange={(v) => setFilters({ ...filters, community: v })} testId="filter-community" />
                <div>
                  <label className="text-xs uppercase tracking-wide text-brand-inkLight">Country</label>
                  <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className="input-elegant mt-1" data-testid="filter-country">
                    <option value="">Any</option>
                    {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
                  </select>
                </div>
                <TextInput label="City" value={filters.location} onChange={(v) => setFilters({ ...filters, location: v })} testId="filter-location" />
                <TextInput label="Education" value={filters.education} onChange={(v) => setFilters({ ...filters, education: v })} testId="filter-education" />
                <TextInput label="Profession" value={filters.profession} onChange={(v) => setFilters({ ...filters, profession: v })} testId="filter-profession" />
                <Select label="Marital status" value={filters.marital_status} onChange={(v) => setFilters({ ...filters, marital_status: v })} options={["", "Never Married", "Divorced", "Widowed", "Awaiting Divorce"]} testId="filter-marital" />
                <Select label="Diet" value={filters.diet} onChange={(v) => setFilters({ ...filters, diet: v })} options={["", "Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan"]} testId="filter-diet" />
              </div>
              <button onClick={load} className="btn-primary w-full mt-5 flex items-center justify-center gap-2" data-testid="filter-apply">
                <SearchIcon className="h-4 w-4" /> Apply filters
              </button>
            </div>
          </aside>

          {/* Results */}
          <main className="md:col-span-9">
            <p className="text-sm text-brand-inkLight mb-4">{total} matches found</p>
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/5] bg-white border border-brand-line rounded-lg animate-pulse" />)}
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-lg border border-brand-line">
                <p className="font-serif text-2xl text-brand-ink">No matches found</p>
                <p className="text-sm text-brand-inkLight mt-2">Try widening your filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-results">
                {results.map((p) => (
                  <ProfileCard
                    key={p.user_id}
                    profile={p}
                    isShortlisted={!!shortlist.find((s) => s.user_id === p.user_id)}
                    onSendInterest={sendInterest}
                    onShortlist={toggleShortlist}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-brand-inkLight">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input-elegant mt-1" data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o || "Any"}</option>)}
      </select>
    </div>
  );
}
function TextInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-brand-inkLight">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="input-elegant mt-1" data-testid={testId} />
    </div>
  );
}
